import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiButton, BasicButton, BasicTable } from '../../assets/common';
import { filter_feedback, sort_table_icon } from '../../assets/img';
import styles from './ListOfUserDetails.module.scss';
import { addCommas, adjustDate, getBreadCrumbList, paginationConfig } from '../../utils/helper';
import CustomFilterTable from '../CustomFilterTable';
import { debounce, isEmpty, sortBy } from 'lodash';
import talentProfileApi from '../../services/talentProfiles';
import {
  ASSIGNMENT_STATUS_TYPES,
  DATE_TIME_FORMAT,
  LIST_OF_USER_DETAIL_TYPE,
  MODULE_FILTER_TABLE,
  MONTH_NAMES,
  USER_ACCESS_FILTER_DATE,
  USER_ACCESS_MESSAGE,
} from '../../utils/constants';
import { notification, Pagination, Tooltip } from 'antd';
import { useSearchParams } from '../../hooks/useSearchQuery';
import BreadCrumb from '../BreadCrumb/BreadCrumb';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';

const INIT_FILTER_PARAMS = {
  roles: [],
  accessLevels: [],
  businessAccess: [],
  employmentStatus: [],
  page: 1,
  limit: 20,
  keyword: '',
  isExport: false,
  roleAccess: [],
  business: [],
  date: {
    type: '',
    month: null,
    year: '',
    quarter: '',
  },
  reportType: '',
  accessCount: '',
};

const INIT_FILTER_OPTIONS = {
  roles: [],
  accessLevels: [],
  businessAccess: [],
  employmentStatus: [],
};

const customStyles = {
  normalLabel: {
    padding: '4px 8px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#000',
  },
  activeLabel: {
    backgroundColor: '#E1F2FE',
    padding: '4px 8px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#00488F',
    borderRadius: '4px',
  },
  inactiveLabel: {
    backgroundColor: '#EBEBEB',
    padding: '4px 8px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#3D3D3D',
    borderRadius: '4px',
  },
};

const ListOfUserDetails = () => {
  const { filterUserDashboad } = useSelector((state) => state.app.adminModule.UserAccessManagement);
  const { type } = useSearchParams();
  const { roleActive } = useSelector((state) => state.user);

  const [filterParams, setFilterParams] = useState(INIT_FILTER_PARAMS);
  const [filtersOption, setFiltersOption] = useState(INIT_FILTER_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [listOfUserDetails, setListOfUserDetails] = useState([]);
  const [totalListOfUser, setTotalListOfUser] = useState(0);
  const [minHeightItem, setMinHeightItem] = useState(0);
  const [ready, setReady] = useState(false);
  const divRefs = useRef({});
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (isEmpty(divRefs?.current)) return;

    const newMap = {};
    Object.keys(divRefs.current).forEach((key) => {
      const el = divRefs.current[key];
      if (el) {
        newMap[key] = el.scrollHeight;
      }
    });

    const allHeights = Object.values(newMap);
    const tallest = Math.max(...allHeights);

    setMinHeightItem(tallest);
    setReady(true);
  }, [listOfUserDetails]);

  const handleSearch = debounce((value) => {
    setFilterParams((prev) => ({ ...prev, keyword: value, page: 1 }));
  }, 500);

  useEffect(() => {
    const getFilterFeedback = async () => {
      try {
        const response = await talentProfileApi.getFilterListOfUserDetails(roleActive);
        if (response?.status === 200 && response?.data?.result) {
          setFiltersOption(response.data.result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    getFilterFeedback();
  }, [setFiltersOption]);

  const getListOfUserDetails = async () => {
    const body = {
      ...filterParams,
      roleAccess: filterUserDashboad?.roles || [],
      business: filterUserDashboad?.business_access || [],
      date: { ...filterUserDashboad?.date, ...adjustDate(filterUserDashboad?.date) },
      reportType: type || null,
    };

    try {
      setLoading(true);
      setTotalListOfUser(0);
      setListOfUserDetails([]);
      const response = await talentProfileApi.getListOfUserDetails(body, roleActive);
      if (response.status === 200 && response?.data?.result?.data) {
        const data = response?.data?.result;
        const tempData = data?.data;
        setListOfUserDetails(tempData);
        setTotalListOfUser(data?.total);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getListOfUserDetails();
  }, [filterParams]);

  const onApplyFilterItem = useCallback((filterName, filters) => {
    setFilterParams((prev) => {
      return {
        ...prev,
        [filterName]: filters,
        page: 1,
      };
    });
  }, []);

  const handleReturnFilter = (data, listSelected, close, filterName, loading) => {
    return (
      <div style={{ position: 'relative' }}>
        <CustomFilterTable
          setSelectedKeys={setFilterParams}
          selectedKeys={listSelected}
          filters={
            (!isEmpty(data) &&
              data.map((item) => ({
                value: item.value,
                text: item.label,
                isChecked: item.value,
              }))) ||
            []
          }
          onApplyFilters={onApplyFilterItem}
          isLoading={loading}
          close={close}
          filterName={filterName}
          moduleName={MODULE_FILTER_TABLE.LIST_OF_USER_DETAILS}
        />
      </div>
    );
  };

  const handleFormatStatus = (status) => {
    let tempLabel = '';
    switch (status) {
      case ASSIGNMENT_STATUS_TYPES.ACTIVE:
        tempLabel = 'Active';
        break;
      case ASSIGNMENT_STATUS_TYPES.INACTIVE:
        tempLabel = 'Inactive';
        break;
      case ASSIGNMENT_STATUS_TYPES.SUSPENDED:
        tempLabel = 'Suspended';
        break;
      default:
        tempLabel = status;
        break;
    }
    return tempLabel;
  };

  const employmentStatusColumn = {
    title: (
      <span style={{ display: 'inline-block', textAlign: 'left' }}>
        Employment <br />
        Status
      </span>
    ),
    dataIndex: 'assignment_status_type',
    align: 'left',
    width: '5%',
    render: (_, record) => {
      return (
        <div style={{ textAlign: 'center' }}>
          {[ASSIGNMENT_STATUS_TYPES.INACTIVE, ASSIGNMENT_STATUS_TYPES.SUSPENDED].includes(record?.assignment_status_type) ? (
            <Tooltip
              placement="top"
              title={
                <>
                  <div>
                    <span
                      style={{
                        color: '#FFFFFF',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '18px',
                      }}
                    >
                      {`${record?.action_name || ''} - ${record?.action_reason || ''}`}
                    </span>
                  </div>
                </>
              }
              overlayClassName="custom-tooltip"
              {...{
                overlayInnerStyle: {
                  width: '150px',
                  fontSize: '12px',
                  lineHeight: '18px',
                  gap: '24px',
                  padding: '12px',
                  borderRadius: '4px',
                  backgroundColor: '#181818',
                },
              }}
            >
              <span
                style={
                  ![ASSIGNMENT_STATUS_TYPES.INACTIVE, ASSIGNMENT_STATUS_TYPES.SUSPENDED].includes(record?.assignment_status_type)
                    ? customStyles.activeLabel
                    : customStyles.inactiveLabel
                }
              >
                {handleFormatStatus(record?.assignment_status_type) || '-'}
              </span>
            </Tooltip>
          ) : (
            <span
              style={
                !isEmpty(record?.assignment_status_type)
                  ? ![ASSIGNMENT_STATUS_TYPES.INACTIVE, ASSIGNMENT_STATUS_TYPES.SUSPENDED].includes(record?.assignment_status_type)
                    ? customStyles.activeLabel
                    : customStyles.inactiveLabel
                  : customStyles.normalLabel
              }
            >
              {handleFormatStatus(record?.assignment_status_type) || '-'}
            </span>
          )}
        </div>
      );
    },
    filterIcon: <img src={filter_feedback} alt="" />,
    filterDropdown: ({ _clearFilters, close }) => {
      const employmentStatusFilterData = !isEmpty(filtersOption?.employmentStatus)
        ? sortBy(filtersOption?.employmentStatus).map((i) => ({
            label: handleFormatStatus(i),
            value: i,
          }))
        : [];

      return handleReturnFilter(employmentStatusFilterData, filterParams.employmentStatus, close, 'employmentStatus', false);
    },
  };

  const accessCountColumn = {
    title: () => (
      <div>
        Access Count{' '}
        <BasicButton
          mode="icon"
          onClick={() =>
            setFilterParams((prev) => ({ ...prev, accessCount: prev.accessCount === '' || prev.accessCount === 'ASC' ? 'DESC' : 'ASC' }))
          }
        >
          <img
            src={sort_table_icon}
            alt="sort"
            className="pl-2 mb-1"
            style={filterParams.accessCount === 'DESC' ? { transform: 'rotateX(180deg)' } : {}}
          />
        </BasicButton>
      </div>
    ),
    dataIndex: 'access_count',
    align: 'left',
    width: '10%',
    render: (_, record) => {
      return <div style={{ textAlign: 'center' }}>{addCommas(record.access_count) || '-'}</div>;
    },
    onCell: (record) => {
      return {
        style: !record?.isFirstRow ? { borderRight: 'none' } : {},
      };
    },
  };

  const removedDateTimeColumn = {
    title: 'Removed Date & Time',
    dataIndex: 'removed_at',
    align: 'left',
    width: '12%',
    render: (_, record) => {
      return (
        <span className={styles.normalText}>{record?.removed_at ? moment(record.removed_at).format(DATE_TIME_FORMAT.DATE_TIME_SLASH) : ''}</span>
      );
    },
  };

  const addedDateTimeColumn = {
    title: 'Added Date & Time',
    dataIndex: 'created_at',
    width: '13%',
    render: (_, record) => {
      return (
        <span className={styles.normalText}>{record?.created_at ? moment(record.created_at).format(DATE_TIME_FORMAT.DATE_TIME_SLASH) : ''}</span>
      );
    },
  };

  const updatedDateTimeColumn = {
    title: 'Updated Date & Time',
    dataIndex: 'updated_at',
    width: '13%',
    render: (_, record) => {
      return (
        <div className={styles.customColumnStyle}>
          {!isEmpty(record?.updated_at)
            ? record?.updated_at.map((i, index) => {
                return (
                  <div key={index} className={styles.normalText}>
                    {i ? moment(i).format(DATE_TIME_FORMAT.DATE_TIME_SLASH) : ''}
                  </div>
                );
              })
            : '-'}
        </div>
      );
    },
  };

  const previousRoleColumn = {
    title: 'Previous Role',
    dataIndex: 'previous_role',
    align: 'left',
    width: '15%',
    render: (_, record) => {
      return (
        <div className={styles.customColumnStyle}>
          {!isEmpty(record?.previous_role)
            ? record?.previous_role.map((i, index) => {
                return (
                  <div key={index} className={styles.normalText}>
                    {i || '-'}
                  </div>
                );
              })
            : '-'}
        </div>
      );
    },
  };

  const currentRoleColumn = {
    title: 'Current Role',
    dataIndex: 'current_role',
    align: 'left',
    width: '15%',
    render: (_, record) => {
      return (
        <div className={styles.customColumnStyle}>
          {!isEmpty(record?.current_role)
            ? record?.current_role.map((i, index) => {
                return (
                  <div key={index} className={styles.normalText}>
                    {i || '-'}
                  </div>
                );
              })
            : '-'}
        </div>
      );
    },
  };

  const columns = [
    {
      title: 'No.',
      dataIndex: 'no',
      align: 'left',
      width: '0.1%',
      render: (_, record) => {
        return <span className={styles.normalText}>{record?.no || ''}</span>;
      },
    },
    {
      title: 'Name',
      dataIndex: 'birth_name',
      align: 'left',
      width: '10%',
      render: (_, record) => {
        return <span style={{ fontWeight: 700, color: '#3D3D3D' }}>{record?.birth_name || '-'}</span>;
      },
    },
    {
      title: 'Staff Id',
      dataIndex: 'staff_id',
      align: 'left',
      width: '5%',
      render: (_, record) => {
        return <span className={styles.normalText}>{record?.staff_id || '-'}</span>;
      },
    },
    {
      title: 'Role',
      dataIndex: 'roles',
      align: 'left',
      width: '12%',
      render: (_, record) => {
        return (
          <div className={styles.customColumnStyle}>
            {!isEmpty(record?.roles)
              ? record?.roles.map((i, index) => {
                  return (
                    <div
                      key={index}
                      className={styles.normalText}
                      style={{
                        minHeight: ready ? `${record?.roles?.[index]?.accessLevels.length * minHeightItem}px` : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {i?.name || '-'}
                    </div>
                  );
                })
              : '-'}
          </div>
        );
      },
      filterIcon: <img src={filter_feedback} alt="" />,
      filterDropdown: ({ _clearFilters, close }) => {
        const roleFilterData = filtersOption?.roles.map((i) => ({
          label: i,
          value: i,
        }));

        return handleReturnFilter(roleFilterData, filterParams.roles, close, 'roles', false);
      },
    },
    {
      title: 'Access Level',
      dataIndex: 'roles',
      align: 'left',
      width: '10%',
      render: (_, record) => {
        return (
          <div className={styles.customColumnStyle}>
            {!isEmpty(record?.roles)
              ? record?.roles?.map((r) => {
                  return r?.accessLevels.map((i, index) => {
                    return (
                      <div
                        key={index}
                        className={styles.normalText}
                        style={{
                          minHeight: ready ? `${minHeightItem}px` : 'none',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {i?.accessLevel || '-'}
                      </div>
                    );
                  });
                })
              : '-'}
          </div>
        );
      },
      filterIcon: <img src={filter_feedback} alt="" />,
      filterDropdown: ({ _clearFilters, close }) => {
        const accessLevelFilterData = filtersOption?.accessLevels.map((i) => ({
          label: i,
          value: i,
        }));

        return handleReturnFilter(accessLevelFilterData, filterParams.accessLevels, close, 'accessLevels', false);
      },
    },
    {
      title: 'Business Access',
      dataIndex: 'roles',
      align: 'left',
      width: '15%',
      render: (_, record, rowIndex) => {
        return (
          <div className={styles.customColumnStyle}>
            {!isEmpty(record?.roles)
              ? record?.roles?.map((r, rIndex) => {
                  return r?.accessLevels.map((i, iIndex) => {
                    const refKey = `${rowIndex}-${rIndex}-${iIndex}`;
                    return (
                      <div
                        key={refKey}
                        className={`${styles.normalText}`}
                        style={{
                          minHeight: ready ? `${minHeightItem}px` : 'none',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        ref={(el) => {
                          if (el) divRefs.current[refKey] = el;
                        }}
                      >
                        {i?.businessAccessName || '-'}
                      </div>
                    );
                  });
                })
              : '-'}
          </div>
        );
      },
      filterIcon: <img src={filter_feedback} alt="" />,
      filterDropdown: ({ _clearFilters, close }) => {
        const buAccessFilterData = filtersOption?.businessAccess.map((i) => ({
          label: i,
          value: i,
        }));

        return handleReturnFilter(buAccessFilterData, filterParams.businessAccess, close, 'businessAccess', false);
      },
    },
  ];

  const pagination = useMemo(() => {
    const onChange = (pageNum) => {
      setFilterParams((prevState) => ({ ...prevState, page: pageNum }));
    };
    return paginationConfig(totalListOfUser || 0, filterParams.page, onChange, filterParams.limit);
  }, [totalListOfUser, filterParams.page, filterParams.limit]);

  const getColumns = useMemo(() => {
    if (type === LIST_OF_USER_DETAIL_TYPE.TOTAL_USERS) {
      return [...columns, employmentStatusColumn];
    } else if (type === LIST_OF_USER_DETAIL_TYPE.ACTIVE_USERS) {
      return [...columns, accessCountColumn, employmentStatusColumn];
    } else if (type === LIST_OF_USER_DETAIL_TYPE.REMOVED_USERS) {
      return [...columns, employmentStatusColumn, removedDateTimeColumn];
    } else if (type === LIST_OF_USER_DETAIL_TYPE.INACTIVE_USERS) {
      return [...columns, accessCountColumn, employmentStatusColumn];
    } else if (type === LIST_OF_USER_DETAIL_TYPE.ADDED_USERS) {
      return [...columns, addedDateTimeColumn];
    } else if (type === LIST_OF_USER_DETAIL_TYPE.UPDATED_USERS) {
      return [...columns.filter((_, idx) => idx < 3), previousRoleColumn, currentRoleColumn, updatedDateTimeColumn];
    } else {
      return columns;
    }
  }, [type, filtersOption, listOfUserDetails, minHeightItem]);

  const getBreadCrumdLastName = useMemo(() => {
    let tempVal = '';
    switch (type) {
      case LIST_OF_USER_DETAIL_TYPE.TOTAL_USERS:
        tempVal = `List of ${LIST_OF_USER_DETAIL_TYPE.TOTAL_USERS}`;
        break;
      case LIST_OF_USER_DETAIL_TYPE.ACTIVE_USERS:
        tempVal = `List of ${LIST_OF_USER_DETAIL_TYPE.ACTIVE_USERS}`;
        break;
      case LIST_OF_USER_DETAIL_TYPE.INACTIVE_USERS:
        tempVal = `List of ${LIST_OF_USER_DETAIL_TYPE.INACTIVE_USERS}`;
        break;
      case LIST_OF_USER_DETAIL_TYPE.ADDED_USERS:
        tempVal = `List of ${LIST_OF_USER_DETAIL_TYPE.ADDED_USERS}`;
        break;
      case LIST_OF_USER_DETAIL_TYPE.UPDATED_USERS:
        tempVal = `List of ${LIST_OF_USER_DETAIL_TYPE.UPDATED_USERS}`;
        break;
      case LIST_OF_USER_DETAIL_TYPE.REMOVED_USERS:
        tempVal = `List of ${LIST_OF_USER_DETAIL_TYPE.REMOVED_USERS}`;
        break;
      default:
        break;
    }
    return tempVal;
  }, [type]);

  const handleBack = () => {
    history.goBack();
  };

  const handleExport = async () => {
    try {
      const body = {
        ...filterParams,
        roleAccess: filterUserDashboad?.roles || [],
        business: filterUserDashboad?.business_access || [],
        date: filterUserDashboad?.date || null,
        reportType: type || null,
        isExport: true,
      };
      setLoadingExport(true);
      const res = await talentProfileApi.getListOfUsersToExport(body, roleActive);
      const fileUrl = res?.data?.result;
      if (res.status === 200 && fileUrl) {
        fetch(fileUrl)
          .then((res) => {
            return res.blob();
          })
          .then((blob) => {
            const href = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('download', getBreadCrumdLastName);
            link.href = href;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          })
          .catch((err) => {
            console.error(err);
            notification.error({
              message: USER_ACCESS_MESSAGE.AN_UNEXPECTED_ERROR,
              duration: 5,
            });
          })
          .finally(() => {
            setLoadingExport(false);
          });
      }
    } catch (error) {
      notification.error({
        message: USER_ACCESS_MESSAGE.AN_UNEXPECTED_ERROR,
        duration: 5,
      });
    } finally {
      setLoadingExport(false);
    }
  };

  const getDateFilterLabel = useMemo(() => {
    if (filterUserDashboad?.date?.type === USER_ACCESS_FILTER_DATE.QUARTER) {
      return `Q${filterUserDashboad?.date?.quarter} ${filterUserDashboad?.date?.year}`;
    } else if (filterUserDashboad?.date?.type === USER_ACCESS_FILTER_DATE.YAER) {
      return `${filterUserDashboad?.date?.year}`;
    } else {
      return filterUserDashboad?.date?.month
        ? `${MONTH_NAMES[Number(filterUserDashboad?.date?.month) - 1]} ${filterUserDashboad?.date?.year}`
        : `${filterUserDashboad?.date?.year}`;
    }
  }, [filterUserDashboad]);

  return (
    <div className={styles.listOfUserContainer}>
      <div style={{ marginBottom: '20px' }}>
        <BreadCrumb level={4} breadCrumbList={getBreadCrumbList([`Dashboard`, `Admin`, `User Access Management`, getBreadCrumdLastName])} />
      </div>
      <h3 className={styles.mainTitle}>{getBreadCrumdLastName} </h3>
      <div className={styles.listFilterLabel}>
        {!isEmpty(filterUserDashboad?.roles) && <span>Role Access: {filterUserDashboad?.roles.join(', ')}</span>}
        {!isEmpty(filterUserDashboad?.business_access) && <span>Business: {filterUserDashboad?.business_access.join(', ')}</span>}
        {!isEmpty(filterUserDashboad?.date?.type) && <span>{getDateFilterLabel}</span>}
      </div>
      <div className={styles.menuWrapper}>
        <div style={{ width: '416px' }} className={styles.searchBox}>
          <input
            name={`search`}
            placeholder={'Search by Staff Name and Staff ID'}
            className={`${styles.searchInput}`}
            onChange={(e) => handleSearch(e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className={styles.groupBtn} style={{ width: '220px' }}>
          <BasicButton style={{ minWidth: '100px', height: '36px' }} onClick={() => handleBack()}>
            Back
          </BasicButton>
          <AiButton
            mode="teal"
            style={{ minWidth: '100px', height: '36px' }}
            onClick={() => handleExport()}
            disabled={loadingExport || !listOfUserDetails.length}
          >
            Export
          </AiButton>
        </div>
      </div>
      <BasicTable
        className={styles.listOfUserDetails}
        rowKey={'id'}
        dataSource={
          !isEmpty(listOfUserDetails) &&
          listOfUserDetails?.map((i, index) => {
            return {
              ...i,
              no: (filterParams.page - 1) * filterParams.limit + (index + 1),
            };
          })
        }
        columns={getColumns}
        pagination={false}
        loading={loading}
        styles={{ headerColor: '#8896A7', borderRadius: 6, sortBackground: 'transparent' }}
        getPopupContainer={(trigger) => trigger.parentElement}
      />
      {!isEmpty(listOfUserDetails) && <Pagination {...pagination} />}
    </div>
  );
};
export default ListOfUserDetails;
